import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { API, SSPContractDTO } from '../../../services/api/api';
import { ErrorService } from '../../../services/error.service';
import { UtilService } from '../../../services/util.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { SSPContractCreationComponent } from '../../../components/sspcontract-creation/sspContract-creation.component';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { SSPSComponent } from '../ssps/ssps.component';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { RequestClass } from '../../../services/global/RequestClass';

type SplitButtonAction = 'edit' | 'deals' | 'delete';

@Component({
  selector: 'sb-page-ssp-contracts',
  templateUrl: './sspContracts.component.html',
  styleUrls: ['./sspContracts.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SSPContractsComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly permissionService = inject(PermissionService);
  private readonly modalService = inject(ModalService);
  private readonly translate = inject(TranslateService);
  private readonly api = inject(API);
  private readonly errorHandler = inject(ErrorService);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);

  // table wrapper variables
  contracts: SSPContractDTO[] = [];

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .shared_SSPContract_retrieveAll(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe({
        next: (receivedContracts: SSPContractDTO[]) => {
          this.contracts = this.buildSplitButtons(receivedContracts);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  deleteContract(sspContract: SSPContractDTO) {
    this.modalService
      .confirm({
        title: this.translate.instant('lang.sspContracts.deleteContract'),
        message: this.translate.instant('lang.sspContracts.deleteContractQuestion', { sspContract: sspContract.ssp.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.loading.set(true);
          this.api
            .shared_SSPContract_deleteSSPContract(sspContract.id)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.requestDebounced();
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  createContract() {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: SSPContractCreationComponent,
          data: {
            useParamData: true,
            editMode: false,
          },
        },
        title: this.translate.instant('lang.globals.createContract'),
        icon: 'plus',
        // modalOptions: {size: 'sm'}
      })
      .then((e: SSPContractDTO) => {
        if (e) {
          this.requestDebounced();
        }
      })
      .catch(() => {});
  }

  editContract(pContract: SSPContractDTO) {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: SSPContractCreationComponent,
          data: {
            sspContract: pContract,
            useParamData: true,
            editMode: true,
          },
        },
        title: this.translate.instant('lang.globals.editContract'),
        icon: 'edit',
        // modalOptions: {size: 'sm'}
      })
      .then((e: SSPContractDTO) => {
        if (e) {
          this.requestDebounced();
        }
      })
      .catch(() => {});
  }

  openSSPs() {
    this.modalService
      .showNavigationModal({
        entryComponent: {
          value: SSPSComponent,
          data: null,
        },
        icon: 'ssp',
        title: this.translate.instant('lang.globals.ssps'),
        modalOptions: { size: 'lg' },
      })
      .then()
      .catch(() => {
        /* needs function call otherwise error */
      });
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.ssp_contract_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.editContract',
          action: 'edit',
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.deal_list])) {
        item.sbButtons.push({
          icon: 'budgetCoin',
          tooltip: 'lang.globals.deals',
          link: RoutingID.deals,
          queryParams: { sspContractIds: item?.id },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.ssp_contract_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.delete',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(sspContract: SSPContractDTO, $event: SplitButtonAction) {
    switch ($event) {
      case 'edit':
        this.editContract(sspContract);
        break;
      case 'delete':
        this.deleteContract(sspContract);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
